import { api } from "@/api";
import { els, state } from "@/state";
import type { PricingPlan } from "@/types";
import { escapeHtml, fmtInt, parseUserRoles } from "@/utils";

export async function loadPricingPlans(): Promise<void> {
  try {
    const data = await api<{ plans: PricingPlan[] }>("/api/pricing-plans");
    state.pricingPlans = data.plans || [];
  } catch {
    state.pricingPlans = [];
  }
}

export function renderLanding(): void {
  const isLoggedIn = Boolean(state.user);
  toggleHidden(els.landingGuestNav, isLoggedIn);
  toggleHidden(els.landingGuestChatEntry, isLoggedIn);
  toggleHidden(els.landingUserBadge, !isLoggedIn);

  if (state.user) {
    if (els.landingUserName) {
      els.landingUserName.textContent = state.user.name || state.user.email || "用户";
    }
    if (els.landingConsoleLink) {
      const roles = parseUserRoles(state.user);
      els.landingConsoleLink.textContent = roles.includes("admin") ? "进入管理后台" : "进入控制台";
    }
  }

  renderPricingPlans();
}

function toggleHidden(element: HTMLElement | null, hidden: boolean): void {
  if (!element) return;
  element.classList.toggle("hidden", hidden);
  element.toggleAttribute("hidden", hidden);
}

function renderPricingPlans(): void {
  const pricingContainer = document.querySelector(".landing-pricing .pricing-grid");
  if (!pricingContainer) return;

  const plans = state.pricingPlans;
  const isLoggedIn = Boolean(state.user);
  if (!plans.length) {
    pricingContainer.innerHTML = `<p class="muted" style="text-align: center; padding: 20px;">加载定价方案失败</p>`;
    return;
  }

  pricingContainer.innerHTML = plans.map((plan) => {
    const features = parseFeatures(plan.features);
    const isFree = plan.price === 0;
    const priceDisplay = isFree ? "¥0" : `¥${plan.price}`;
    const faceAmount = Number(plan.faceAmount ?? plan.price);
    const hasDiscountFace = faceAmount > Number(plan.price);
    const actionLabel = isFree
      ? (isLoggedIn ? "立即领取" : "注册领取")
      : plan.productType === "monthly_pass" ? "购买月卡" : "立即充值";
    const pointsDisplay = Number(plan.tokenAmount) > 0
      ? `<p class="muted">${fmtInt(plan.tokenAmount)} 点${plan.productType === "monthly_pass" ? ` · 启用后 ${plan.validityMonths || 1} 个自然月` : ""}</p>`
      : "";

    return `
      <div class="pricing-card ${plan.isFeatured ? "pricing-card-featured" : ""}">
        <p class="eyebrow">${escapeHtml(plan.name)}</p>
        <h3>${escapeHtml(plan.description || plan.name)}</h3>
        <p class="price">${priceDisplay}</p>
        <p class="muted">${plan.productType === "monthly_pass" ? "购买后手动启用" : "到账后进入钱包"}</p>
        ${pointsDisplay}
        ${hasDiscountFace ? `<p class="muted">票面 ¥${faceAmount.toFixed(2)}</p>` : ""}
        <ul>
          ${features.map((feature) => `<li>${escapeHtml(feature)}</li>`).join("")}
        </ul>
        ${isLoggedIn
          ? `<button type="button" class="${isFree ? "secondary-button" : "primary-button"} full-button" data-action="buy-plan" data-id="${escapeHtml(plan.id)}">${actionLabel}</button>`
          : `<a href="${isFree ? "/register/" : "/login/"}" class="${isFree ? "secondary-button" : "primary-button"} full-button">${actionLabel}</a>`}
      </div>
    `;
  }).join("");
}

function parseFeatures(featuresStr: string): string[] {
  if (!featuresStr) return [];
  try {
    const parsed = JSON.parse(featuresStr);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
